<template>
  <TransitionRoot appear :show="show">
    <Dialog
      as="div"
      open
      class="fixed top-0 -left-[2px] z-55 h-screen w-full overflow-y-auto tracking-[0.2px] subpixel-antialiased"
      @close="emit('close')"
    >
      <TransitionChild
        as="template"
        enter="duration-200 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay
          class="ds-backdrop fixed top-0 left-0 h-full w-full bg-black/50"
        />
      </TransitionChild>

      <TransitionChild
        as="template"
        enter="duration-200 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="h-screen overflow-y-auto">
          <div
            :class="[
              'flex min-h-screen justify-center py-10',
              verticalPosition === 'top' ? 'items-start' : 'items-center',
            ]"
          >
            <div
              :class="[
                'relative max-w-[95%] rounded-[10px] bg-white shadow-[0px_6px_19px_0px_rgba(182,182,182,0.35)] ds:rounded-(--ds-radius-xl) ds:bg-(--ds-surface-elevated) ds:shadow-(--ds-elevation-overlay)',
                wrapperClass,
              ]"
              :style="{
                width: !width
                  ? 'auto'
                  : typeof width !== 'number'
                    ? width
                    : width + 'px',
              }"
            >
              <div
                :class="[
                  'ds-alert__body p-[17px_17px_25px_17px] ds:px-7 ds:pt-[30px] ds:pb-[26px]',
                  bodyClass,
                ]"
              >
                <!-- Design-system look: the design system's confirm symbol (illustration "attention"). -->
                <img
                  v-if="showAlertIcon"
                  :src="
                    Vapor.asset(
                      designSystem
                        ? 'images/ds-illustrations/attention.svg'
                        : 'images/attention.png',
                    )
                  "
                  alt=""
                  class="mx-auto my-[15px] h-[175px] w-[175px] ds:mt-0 ds:mb-3.5 ds:size-24"
                />
                <slot>
                  <div :class="['text-center', contentClass]">
                    <h3
                      class="mb-[10px] text-2xl font-semibold text-fg-muted ds:text-[20px] ds:font-bold ds:tracking-[-0.3px] ds:text-(--ds-text-primary)"
                      v-text="title || $t('common.are_you_sure')"
                    />
                    <!-- eslint-disable vue/no-v-html -->
                    <p
                      v-if="renderAsHtml"
                      class="text-sm font-light text-fg-muted ds:text-[15px] ds:leading-[1.55] ds:font-normal ds:text-(--ds-text-secondary)"
                      v-html="message"
                    />
                    <p
                      v-else
                      class="text-sm font-light text-fg-muted ds:text-[15px] ds:leading-[1.55] ds:font-normal ds:text-(--ds-text-secondary)"
                      v-text="message"
                    />
                  </div>
                </slot>
                <slot name="buttons">
                  <div
                    class="mt-6 flex items-center justify-center gap-2 ds:mt-[22px] ds:gap-2.5"
                  >
                    <button
                      v-if="displayCancelButton"
                      type="button"
                      class="h-[35px] min-w-[100px] rounded-[5px] bg-secondary px-[25px] text-[13px] text-primary outline-none ds:h-(--ds-button-height) ds:min-w-[120px] ds:px-(--ds-button-padding-x) ds:rounded-(--ds-radius-sm) ds:border ds:border-(--ds-action-secondary-border) ds:bg-(--ds-surface-default) ds:text-(length:--ds-button-font-size) ds:font-medium ds:text-(--ds-action-secondary-text) ds:hover:border-(--ds-action-secondary-hover-border)"
                      @click="emit('close')"
                      v-text="cancelText || $t('common.cancel')"
                    />
                    <button
                      type="button"
                      class="h-[35px] min-w-[100px] rounded-[5px] bg-primary px-[25px] text-[13px] text-white outline-none disabled:opacity-50 ds:h-(--ds-button-height) ds:min-w-[120px] ds:px-(--ds-button-padding-x) ds:rounded-(--ds-radius-sm) ds:bg-(--ds-action-brand) ds:text-(length:--ds-button-font-size) ds:font-medium ds:text-(--ds-action-brand-text) ds:hover:bg-(--ds-action-brand-hover)"
                      :disabled="confirmLoading"
                      @click="emit('confirm')"
                    >
                      <FontAwesomeIcon
                        v-if="confirmLoading"
                        :icon="['fal', 'spinner']"
                        class="text-sm text-white"
                        spin
                      />

                      <template v-else>
                        <FontAwesomeIcon
                          v-if="confirmIcon"
                          :icon="['fal', confirmIcon]"
                          class="text-sm text-white mr-2"
                        />

                        <span v-text="confirmText || $t('common.yes')" />
                      </template>
                    </button>
                  </div>
                </slot>
              </div>
            </div>
          </div>
        </div>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogOverlay,
} from '@headlessui/vue'
import Vapor from 'laravel-vapor'
import { useDesignSystem } from '@/composables/designSystem'

defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  title: {
    type: [String, null],
    default: null,
  },
  message: {
    type: [String, null],
    default: null,
  },
  renderAsHtml: {
    type: Boolean,
    default: false,
  },
  width: {
    type: [Number, String, null],
    default: 478,
  },
  bodyClass: {
    type: String,
    default: '',
  },
  contentClass: {
    type: String,
    default: '',
  },
  wrapperClass: {
    type: String,
    default: '',
  },
  verticalPosition: {
    type: String,
    default: 'center',
  },
  showAlertIcon: {
    type: Boolean,
    default: true,
  },
  confirmText: {
    type: String,
    required: false,
    default: null,
  },
  confirmIcon: {
    type: [String, null],
    required: false,
    default: null,
  },
  confirmLoading: {
    type: Boolean,
    required: false,
    default: false,
  },
  cancelText: {
    type: String,
    required: false,
    default: null,
  },
  displayCancelButton: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['close', 'confirm'])

const designSystem = useDesignSystem()
</script>
